import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { fetchProducts, toggleProductStatus, toggleProductPopular } from "../../../slice/product/productSlice";
const BASE_URL = process.env.REACT_APP_API_URL;

const TABS = [
  { key: "normal", label: "Normal Product" },
  { key: "custom", label: "Custom Product" },
];

const ProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { products, loading, error, pagination } = useSelector((s) => s.product);

  const [activeTab, setActiveTab]   = useState("normal");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage]             = useState(1);
  const limit = 10;

  useEffect(() => {
    dispatch(fetchProducts({ page, limit, search: searchTerm, type: activeTab }));
  }, [dispatch, page, searchTerm, activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
  };

  const handleToggleStatus = (id) => dispatch(toggleProductStatus(id));
  const handleTogglePopular = (id) => dispatch(toggleProductPopular(id));

  const resolveImageSrc = (p) => {
    const raw = p.images?.[0] || p.customization?.[0]?.image;
    if (!raw) return "/assets/images/placeholder.png";
    return raw.startsWith("http") ? raw : `${BASE_URL}/uploads/products/${raw}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const isCustomTab = activeTab === "custom";

  return (
    <div style={{ background: "#f6f7fb", minHeight: "100vh", padding: "28px 32px" }}>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, color: "#111827" }}>Product</h1>
          <p style={{ margin: "6px 0 0", color: "#9ca3af", fontSize: 14 }}>
            {pagination.total ?? products.length} products
          </p>
        </div>
      </div>

      {/* ── Search + Tabs ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{
          flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 10,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "12px 16px",
        }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#9aa0ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            style={{ border: "none", outline: "none", flex: 1, fontSize: 14, background: "transparent" }}
          />
        </div>

        {TABS.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                padding: "12px 20px",
                borderRadius: 12,
                border: active ? "none" : "1px solid #e5e7eb",
                background: active ? "#111827" : "#fff",
                color: active ? "#fff" : "#374151",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #eef0f4", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>Loading...</div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#dc2626" }}>{error}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafbfc" }}>
                  {[
                    "Si No", "IMAGE", "NAME", "VENDOR", "CATEGORY",
                    ...(isCustomTab ? ["CUSTOM ITEMS"] : []),
                    "MRP", "SELLING PRICE", "STOCK", "STATUS", "POPULAR", "ACTIONS",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "14px 20px", fontSize: 11,
                        fontWeight: 700, letterSpacing: 0.5, color: "#9ca3af",
                        borderBottom: "1px solid #eef0f4", whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={isCustomTab ? 11 : 10} style={{ textAlign: "center", padding: "50px 0", color: "#aaa" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "#bbb" }}>
                          No {isCustomTab ? "custom" : "normal"} products found
                        </span>
                        <span style={{ fontSize: 12, color: "#d1d5db" }}>
                          {searchTerm ? `No results for "${searchTerm}"` : "Add your first product using the button above"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  products.map((p, idx) => (
                    <tr key={p._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {(page - 1) * limit + idx + 1}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <div style={{
                          width: 38, height: 38, borderRadius: 10, background: "#f3f4f6",
                          overflow: "hidden", flexShrink: 0,
                        }}>
                          <img
                            src={resolveImageSrc(p)}
                            alt={p.productName}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 14, fontWeight: 600, color: "#111827" }}>
                        {p.productName}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {p.vendor_id?.vendorName || "—"}
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>
                        {p.category?.name || "—"}
                      </td>
                      {isCustomTab && (
                        <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563", maxWidth: 220 }}>
                          {p.customization?.length
                            ? p.customization.map((c) => c.flowers.join(", ")).join(" | ")
                            : "—"}
                        </td>
                      )}
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>${p.mrp}</td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>${p.sellingPrice}</td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "#4b5563" }}>{p.stock}</td>
                      <td style={{ padding: "16px 20px" }}>
                        <label className="switch">
                          <input type="checkbox" checked={p.status} onChange={() => handleToggleStatus(p._id)} />
                          <span className="slider round" />
                        </label>
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <label className="switch">
                          <input type="checkbox" checked={p.isPopular} onChange={() => handleTogglePopular(p._id)} />
                          <span className="slider round" />
                        </label>
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <button
                          onClick={() => navigate(`/admin/products/view/${p._id}`)}
                          style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: "#111827", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "16px 20px" }}>
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer" }}
            >Prev</button>
            {Array.from({ length: pagination.totalPages }, (_, i) => (
              <button key={i + 1} onClick={() => setPage(i + 1)}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: page === i + 1 ? "none" : "1px solid #e5e7eb",
                  background: page === i + 1 ? "#111827" : "#fff",
                  color: page === i + 1 ? "#fff" : "#374151",
                  cursor: "pointer",
                }}
              >{i + 1}</button>
            ))}
            <button disabled={page === pagination.totalPages} onClick={() => setPage((p) => p + 1)}
              style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: page === pagination.totalPages ? "not-allowed" : "pointer" }}
            >Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;